import * as Api from '@ViewModels';
import type { ServerSentEventMessage } from 'fetch-event-stream';
import type * as React from 'react';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { COACH_ASSISTANT_NAME } from './prompts';
import type { CoachUpstreamMessage } from './types';

export type CoachStreamVariables = { convoId: string; requestId: string; chatRequest: Api.AIChatRequest };

type CoachStreamReply = { text: string; isDelta: boolean; id?: string };

export const nowId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const parseResponseEvent = (data: string): CoachStreamReply | null => {
	let parsed: { message?: { text?: string }; isDelta?: boolean; Value?: { Id?: string } };
	try {
		parsed = JSON.parse(data);
	} catch {
		return null;
	}
	const text = parsed?.message?.text;
	if (!text) return null;
	return { text, isDelta: !!parsed.isDelta, id: parsed.Value?.Id };
};

// Keyed to the stream, not the last message: transcript lines land mid-reply during a call, and
// "append to the last message" split one reply into two there.
const upsertReply = (
	prev: CoachUpstreamMessage[],
	requestId: string,
	reply: CoachStreamReply
): CoachUpstreamMessage[] => {
	const index = prev.findIndex(m => m.role === 'assistant' && m.requestId === requestId);
	if (index >= 0) {
		const existing = prev[index];
		const text = reply.isDelta ? existing.text + reply.text : reply.text;
		return [...prev.slice(0, index), { ...existing, text }, ...prev.slice(index + 1)];
	}
	return [
		...prev,
		{
			id: reply.id || nowId('msg'),
			text: reply.text,
			timestamp: new Date(),
			isStreaming: true,
			role: 'assistant',
			requestId,
		},
	];
};

export const finalizeReply = (prev: CoachUpstreamMessage[], requestId: string): CoachUpstreamMessage[] =>
	prev.map(m => (m.requestId === requestId && m.isStreaming ? { ...m, isStreaming: false } : m));

export type UseCoachConversationStreamOptions = {
	/** Events, completions and failures of a stream for any other conversation are dropped. */
	conversationIdRef: React.RefObject<string | null>;
	setMessages: React.Dispatch<React.SetStateAction<CoachUpstreamMessage[]>>;
	/** Called after each `Response` token lands in `setMessages`. */
	onReply?: (variables: CoachStreamVariables) => void;
	/** Any non-`Response` event, e.g. `Error`. */
	onOtherEvent?: (event: ServerSentEventMessage, variables: CoachStreamVariables) => void;
	/** Called when the stream ends, before its reply is marked as no longer streaming. */
	onSuccess?: (variables: CoachStreamVariables) => void;
	onError?: (error: Error, variables: CoachStreamVariables) => void;
};

/**
 * SSE transport for the coach assistant's conversation stream endpoint, plus the reducer that folds
 * `Response` tokens into the caller's message list by `requestId`.
 */
export function useCoachConversationStream({
	conversationIdRef,
	setMessages,
	onReply,
	onOtherEvent,
	onSuccess,
	onError,
}: UseCoachConversationStreamOptions) {
	const userSession = useUserSession();
	const isStale = (variables: CoachStreamVariables) => variables.convoId !== conversationIdRef.current;

	return useStreamingResponseMutation<CoachStreamVariables>({
		mutationFn: ({ convoId, chatRequest }) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${COACH_ASSISTANT_NAME}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, variables) => {
			// A reset races the in-flight fetch, so this guard is the backstop against a previous
			// conversation's stream appending into the current one.
			if (isStale(variables)) return;
			if (event.event !== 'Response') {
				onOtherEvent?.(event, variables);
				return;
			}
			const reply = parseResponseEvent(event.data);
			if (!reply) return;
			setMessages(prev => upsertReply(prev, variables.requestId, reply));
			onReply?.(variables);
		},
		onSuccess: variables => {
			if (isStale(variables)) return;
			onSuccess?.(variables);
			setMessages(prev => finalizeReply(prev, variables.requestId));
		},
		onError: (error, variables) => {
			if (isStale(variables)) return;
			onError?.(error, variables);
		},
	});
}
